import { decodeJwtPayload } from './keycloakTokenStorage';

type JwtRealmAccessPayload = {
	realm_access?: {
		roles?: string[];
	};
};

type JwtResourceAccessPayload = {
	resource_access?: Record<string, { roles?: string[] }>;
};

/** Keycloak realm role required for hub launcher access (e.g. `app:machines`). */
export type AppAccessRequirement = string;

/** Extracts Keycloak realm roles from a JWT access token payload. */
export function getRealmRolesFromToken(accessToken: string): string[] {
	const payload = decodeJwtPayload(accessToken) as JwtRealmAccessPayload | undefined;
	const roles = payload?.realm_access?.roles;

	return Array.isArray(roles) ? roles : [];
}

/** Extracts client roles for a Keycloak API client from a JWT access token payload. */
export function getClientRolesFromToken(accessToken: string, clientId: string): string[] {
	const payload = decodeJwtPayload(accessToken) as JwtResourceAccessPayload | undefined;
	const roles = payload?.resource_access?.[clientId]?.roles;

	return Array.isArray(roles) ? roles : [];
}

/** Returns whether the token includes the given Keycloak realm role. */
export function hasRealmRole(accessToken: string, role: string): boolean {
	return getRealmRolesFromToken(accessToken).includes(role);
}

/** Returns whether the token includes the given client role on a Keycloak API client. */
export function hasClientRole(accessToken: string, clientId: string, role: string): boolean {
	return getClientRolesFromToken(accessToken, clientId).includes(role);
}

/** Whether the token grants a hub launcher app role (Keycloak realm role, e.g. `app:machines`). */
export function hasAppRole(accessToken: string, appRole: string): boolean {
	return hasRealmRole(accessToken, appRole);
}

/**
 * Returns whether the caller may access a hub launcher app.
 * When `requiredRole` is omitted, access is always granted.
 */
export function canAccessApp(
	accessToken: string | undefined,
	requiredRole?: AppAccessRequirement,
): boolean {
	if (!requiredRole) {
		return true;
	}

	if (!accessToken) {
		return false;
	}

	return hasAppRole(accessToken, requiredRole);
}
